import React from "react";
import { utcMidnightToday, ymd } from "../utilities/DateUtilities";

export type DateRangeToolbarProps = {
  startDate: Date;
  endDate: Date;
  onChange: (start: Date, end: Date, replace?: boolean) => void;
  presets?: Array<{ label: string; days: number }>;
  className?: string;
};

export default function DateRangeToolbar({
  startDate,
  endDate,
  onChange,
  presets = [
    { label: "Last 7 days", days: 7 },
    { label: "14 days", days: 14 },
    { label: "30 days", days: 30 },
  ],
  className = "",
}: DateRangeToolbarProps) {
  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value; // "YYYY-MM-DD"
    if (!value) return;
    const d = new Date(value + "T00:00:00.000Z");
    // ensure start <= end
    const nextEnd = endDate < d ? d : endDate;
    onChange(d, nextEnd, true);
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) return;
    const d = new Date(value + "T00:00:00.000Z");
    // ensure start <= end
    const nextStart = startDate > d ? d : startDate;
    onChange(nextStart, d, true);
  };

  const setPreset = (days: number) => {
    const end = utcMidnightToday();
    const start = new Date(end);
    start.setUTCDate(end.getUTCDate() - days);
    onChange(start, end, true);
  };

  return (
    <form className={`card p-4 ${className}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        {/* Start date */}
        <div>
          <label className="block mb-1">Start date</label>
          <input
            type="date"
            value={ymd(startDate)}
            onChange={handleStartChange}
            max={ymd(endDate)}
            className="w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-white placeholder-white/60
                       focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40
                       [&::-webkit-calendar-picker-indicator]:invert
                       [&::-webkit-calendar-picker-indicator]:opacity-100
                       [&::-webkit-calendar-picker-indicator]:cursor-pointer"
          />
        </div>

        {/* End date */}
        <div>
          <label className="block mb-1">End date</label>
          <input
            type="date"
            value={ymd(endDate)}
            onChange={handleEndChange}
            min={ymd(startDate)}
            max={ymd(utcMidnightToday())}
            className="w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-white placeholder-white/60
                       focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40
                       [&::-webkit-calendar-picker-indicator]:invert
                       [&::-webkit-calendar-picker-indicator]:opacity-100
                       [&::-webkit-calendar-picker-indicator]:cursor-pointer"
          />
        </div>

        {/* Quick presets */}
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => setPreset(p.days)}
              className="rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white
                         hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </form>
  );
}
